import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthtoken, useSelectedOrganizationId } from 'stores/useAuthStore';
import { useOrganizationsList } from 'stores/useOrganizationsStore';

/**
 * Used to signal to the consuming component that it should update its data,
 * as the organization/auth token has changed.
 *
 * @returns [isOrgMismatch, resolveMismatch]
 */
export function useIsOrgMismatch(): [boolean, () => void] {
  const organizationId = useSelectedOrganizationId();
  const authToken = useAuthtoken();
  const organizations = useOrganizationsList();

  const [prevOrganizationId, setPrevOrganizationId] = useState(organizationId);
  const [prevAuthToken, setPrevAuthToken] = useState(authToken);

  const isEntityWithNoOrgs = useMemo(
    () => !!organizations.lastUpdated && organizations.values.length === 0,
    [organizations]
  );

  const isOrgMismatch = useMemo(
    () =>
      (!!organizationId || isEntityWithNoOrgs) &&
      prevOrganizationId !== organizationId &&
      prevAuthToken !== authToken,
    [
      prevOrganizationId,
      organizationId,
      prevAuthToken,
      authToken,
      isEntityWithNoOrgs,
    ]
  );

  const resolveMismatch = useCallback(() => {
    setPrevOrganizationId(organizationId);
    setPrevAuthToken(authToken);
  }, [organizationId, authToken]);

  useEffect(() => {
    if (organizationId === prevOrganizationId && authToken !== prevAuthToken) {
      // There are a number of cases where the authToken can change and not
      // necessarily be the result of an entity/org change. To prevent this hook
      // from evaluating isOrgMismatch incorrectly, we account for those cases here.
      setPrevAuthToken(authToken);
    }
  }, [authToken]);

  return [isOrgMismatch, resolveMismatch];
}
